import {
  carePlanTopicKindCoding,
  getScreeningModality,
  SCREENING_CATEGORY_NAMES,
  SCREENING_MODALITY_NAMES,
  type ScreeningCategoryCode,
  type ScreeningModalityCode,
} from '@liza/fhir-utils';
import type {
  Coding,
  DataRequirement,
  PlanDefinition,
  PlanDefinitionAction,
  RelatedArtifact,
} from '@medplum/fhirtypes';
import { applicable, every, evidence, itemKind, practitioner, taskCategory } from './builders';

export const PREVENTION_SCREENING_URL = 'https://liza.health/fhir/PlanDefinition/prevention-screening';
export const PREVENTION_SCREENING_VERSION = '1.0.0';
/** Version-pinned canonical, the value the Prevention topic and its RequestGroup carry. */
export const PREVENTION_SCREENING_CANONICAL = `${PREVENTION_SCREENING_URL}|${PREVENTION_SCREENING_VERSION}`;
/** The `care-plan-topic-kind` the topic this protocol owns is marked with. */
export const PREVENTION_TOPIC_KIND = 'prevention' as const;

/**
 * The USPSTF recommendation statements this protocol is authored from, each checked against the
 * published page on 2026-09-19: date, grade, population and interval as the Task Force states them.
 */
export const USPSTF = {
  colorectal: {
    title: 'USPSTF Colorectal Cancer: Screening (final recommendation statement, May 18, 2021)',
    url: 'https://www.uspreventiveservicestaskforce.org/uspstf/recommendation/colorectal-cancer-screening',
  },
  breast: {
    title: 'USPSTF Breast Cancer: Screening (final recommendation statement, April 30, 2024)',
    url: 'https://www.uspreventiveservicestaskforce.org/uspstf/recommendation/breast-cancer-screening',
  },
  cervical: {
    title: 'USPSTF Cervical Cancer: Screening (final recommendation statement, August 21, 2018)',
    url: 'https://www.uspreventiveservicestaskforce.org/uspstf/recommendation/cervical-cancer-screening',
  },
} as const;

const citation = (source: { title: string; url: string }, locus: string): RelatedArtifact[] => [
  { type: 'citation', display: `${source.title}, ${locus}`, url: source.url },
];

const derivedFrom = (source: { title: string; url: string }): RelatedArtifact => ({
  type: 'derived-from',
  display: source.title,
  url: source.url,
});

/**
 * The variables this protocol binds before evaluating any condition. Age and sex gate every action;
 * the two `coveredBy` variables carry the category logic the guideline leaves implicit — that a
 * colorectal screening by any one modality covers the category for that modality's interval, so the
 * other modalities are not also due.
 */
export const PREVENTION_CONTEXT_VARIABLES = [
  { name: '%ageYears', description: 'Integer age in years from Patient.birthDate.' },
  {
    name: '%sex',
    description:
      "String: 'female' | 'male' | 'other' | 'unknown', from the Sex Parameter for Clinical Use, else recorded birth sex, else administrative gender — first present wins; empty when none is recorded or the clinical parameter says no single setting applies.",
  },
  {
    name: '%colorectalCoveredBy',
    description:
      'String: the colorectal screening modality whose most recent study still covers the patient (its date plus its interval is in the future) — the one covering longest when several do; empty when none does.',
  },
  {
    name: '%cervicalCoveredBy',
    description:
      'String: the cervical screening modality whose most recent study still covers the patient; empty when none does.',
  },
] as const;

export type PreventionContextVariable = (typeof PREVENTION_CONTEXT_VARIABLES)[number]['name'];

/**
 * The name stems a modality may be anchored on with a `code:text` search, for the studies production
 * charts file by name alone (a quarter of colonoscopies carry no usable code). Curated here rather
 * than read from the concept table's aliases: the table's aliases are matched word-bounded on a
 * record already fetched, where an exclusion list can veto a hit, but a `:text` search is a
 * *contains* the server runs with no boundary and no exclusion (the production audit found stems like
 * "colonoscop" matching this way), and the anchor takes
 * the newest hit as the last study. So only stems that cannot sit inside another study's name are
 * used — the table's bare `pap` would find "human papillomavirus", `fit` would find "fitness", and
 * `bi-rads` sits on breast MRI reports too. One overlap is accepted and pinned by a test: a CT
 * colonography filed as "virtual colonoscopy" also anchors colonoscopy, which errs towards a longer
 * interval on a study that is absent from production charts today.
 */
export const TEXT_ANCHORS: Partial<Record<ScreeningModalityCode, readonly string[]>> = {
  colonoscopy: ['colonoscop'],
  mammogram: ['mammogra', 'tomosynthesis'],
  pap: ['pap smear', 'pap test', 'papanicolaou', 'thin prep', 'thinprep', 'cervical cytolog', 'cervical smear'],
};

const asCodings = (codings: readonly { system?: string; code?: string }[]): Coding[] =>
  codings.map((c) => ({ system: c.system, code: c.code }));

/**
 * What satisfies a screening: the modality's study codes on a Procedure or a report, its result codes
 * on an Observation, and — where {@link TEXT_ANCHORS} allows — its name stems on a Procedure or a
 * report. The codes come from the screening concept table, so what the protocol anchors on and what
 * the care plan's screening rows are grouped by cannot drift apart.
 */
export const screeningEvidence = (modality: ScreeningModalityCode): DataRequirement[] => {
  const spec = getScreeningModality(modality);
  const requirements: DataRequirement[] = [];
  if (spec.studyCodes.length > 0) {
    requirements.push(evidence('Procedure', asCodings(spec.studyCodes)));
    requirements.push(evidence('DiagnosticReport', asCodings(spec.studyCodes)));
  }
  if (spec.resultCodes.length > 0) requirements.push(evidence('Observation', asCodings(spec.resultCodes)));
  const stems = (TEXT_ANCHORS[modality] ?? []).map((stem) => ({ code: stem }));
  if (stems.length > 0) {
    requirements.push(evidence('Procedure', stems, 'code:text'));
    requirements.push(evidence('DiagnosticReport', stems, 'code:text'));
  }
  return requirements;
};

/** A modality applies only when nothing in its category covers the patient, or it is the one that does. */
const notCoveredByAnother = (
  variable: '%colorectalCoveredBy' | '%cervicalCoveredBy',
  modality: ScreeningModalityCode,
): string => `(${variable}.empty() or ${variable} = '${modality}')`;

interface ScreeningActionSpec {
  modality: ScreeningModalityCode;
  category: ScreeningCategoryCode;
  /** Whole years between studies, as the guideline states it. */
  everyYears: number;
  condition: string;
  conditionDescription: string;
  description: string;
  documentation: RelatedArtifact[];
}

const screening = (spec: ScreeningActionSpec): PlanDefinitionAction => ({
  id: spec.modality,
  title: `${SCREENING_CATEGORY_NAMES[spec.category]} — ${SCREENING_MODALITY_NAMES[spec.modality]}`,
  description: spec.description,
  participant: practitioner,
  extension: [itemKind('screening'), taskCategory('clinical')],
  condition: applicable(spec.condition, spec.conditionDescription),
  input: screeningEvidence(spec.modality),
  timingTiming: every(spec.everyYears, 'a'),
  documentation: spec.documentation,
});

// --- colorectal: adults 45–75, any one modality at its own interval --------------------------------

const COLORECTAL_AGE = '%ageYears >= 45 and %ageYears <= 75';
const COLORECTAL_AGE_TEXT = 'Aged 45 to 75';

const colorectal = (
  modality: ScreeningModalityCode,
  everyYears: number,
  interval: string,
  locus: string,
): PlanDefinitionAction =>
  screening({
    modality,
    category: 'colorectal-cancer',
    everyYears,
    condition: `${COLORECTAL_AGE} and ${notCoveredByAnother('%colorectalCoveredBy', modality)}`,
    conditionDescription: `${COLORECTAL_AGE_TEXT}, and not covered by another colorectal screening still within its interval`,
    description: `${SCREENING_MODALITY_NAMES[modality]} ${interval}, from age 45 to 75 (grade B for 45–49, grade A for 50–75).`,
    documentation: citation(USPSTF.colorectal, locus),
  });

const colonoscopy = colorectal('colonoscopy', 10, 'every 10 years', 'Table 1: colonoscopy every 10 years');
const fit = colorectal('fit', 1, 'every year', 'Table 1: FIT every year');
const stoolDna = colorectal(
  'stool-dna',
  3,
  'every 1 to 3 years (tracked at 3)',
  'Table 1: sDNA-FIT every 1 to 3 years',
);
const ctColonography = colorectal('ct-colonography', 5, 'every 5 years', 'Table 1: CT colonography every 5 years');
const flexibleSigmoidoscopy = colorectal(
  'flexible-sigmoidoscopy',
  5,
  'every 5 years',
  'Table 1: flexible sigmoidoscopy every 5 years',
);
const fobt = colorectal('fobt', 1, 'every year', 'Table 1: high-sensitivity gFOBT every year');

// --- breast: females 40–74, biennial mammography -----------------------------------------------------

const mammogram = screening({
  modality: 'mammogram',
  category: 'breast-cancer',
  everyYears: 2,
  condition: "%sex = 'female' and %ageYears >= 40 and %ageYears <= 74",
  conditionDescription: 'Female, aged 40 to 74',
  description: 'Screening mammography every 2 years from age 40 to 74 (grade B).',
  documentation: citation(USPSTF.breast, 'biennial screening mammography for women aged 40 to 74 years'),
});

// --- cervical: females 21–65; cytology from 21, hrHPV from 30, whichever was done covers -------------

const pap = screening({
  modality: 'pap',
  category: 'cervical-cancer',
  everyYears: 3,
  condition: `%sex = 'female' and %ageYears >= 21 and %ageYears <= 65 and ${notCoveredByAnother('%cervicalCoveredBy', 'pap')}`,
  conditionDescription: 'Female, aged 21 to 65, and not covered by an hrHPV test still within its interval',
  description: 'Cervical cytology every 3 years from age 21 to 65 (grade A).',
  documentation: citation(USPSTF.cervical, 'cytology alone every 3 years, ages 21 to 65'),
});

const hpv = screening({
  modality: 'hpv',
  category: 'cervical-cancer',
  everyYears: 5,
  condition: `%sex = 'female' and %ageYears >= 30 and %ageYears <= 65 and ${notCoveredByAnother('%cervicalCoveredBy', 'hpv')}`,
  conditionDescription: 'Female, aged 30 to 65, and not covered by cytology still within its interval',
  description: 'hrHPV testing, alone or with cytology, every 5 years from age 30 to 65 (grade A).',
  documentation: citation(USPSTF.cervical, 'hrHPV testing alone or cotesting every 5 years, ages 30 to 65'),
});

/**
 * Cancer screening for every patient, from the USPSTF recommendation statements: one action per
 * screening modality, gated on age and sex, anchored on the last study of that modality however it
 * reached the chart, at the interval the guideline gives it. A person-level protocol — it declares no
 * diagnosis focus, applies to everyone, and owns the Prevention topic.
 *
 * The description is patient- and clinician-facing on the topic, so it names what this version does
 * not account for rather than leaving the gaps implicit.
 */
export const preventionScreening: PlanDefinition = {
  resourceType: 'PlanDefinition',
  url: PREVENTION_SCREENING_URL,
  version: PREVENTION_SCREENING_VERSION,
  name: 'PreventionScreening',
  title: 'Prevention',
  status: 'active',
  experimental: false,
  date: '2026-09-19',
  publisher: 'Liza Health',
  type: {
    coding: [{ system: 'http://terminology.hl7.org/CodeSystem/plan-definition-type', code: 'clinical-protocol' }],
  },
  description: [
    'Guideline-recommended cancer screening for every patient, from the US Preventive Services Task Force: ',
    'colorectal cancer screening from age 45 to 75 (colonoscopy every 10 years, FIT or gFOBT every year, ',
    'stool DNA every 3 years, CT colonography or flexible sigmoidoscopy every 5 years), breast cancer screening ',
    'by mammography every 2 years for females aged 40 to 74, and cervical cancer screening for females aged 21 to 65 ',
    '(cytology every 3 years; hrHPV testing every 5 years from age 30). Any one modality done within its interval ',
    'covers its category. Sex is read from the sex parameter for clinical use where recorded, else birth sex, ',
    'else administrative gender.',
    '\n\nNot yet accounted for in this version: ',
    'exclusions the guidelines state (hysterectomy with removal of the cervix, prior high-grade cervical lesions, ',
    'adequate prior screening past 65), ',
    'higher-risk pathways (family history, prior adenomas or inflammatory bowel disease, prior breast cancer or ',
    'chest radiation), ',
    'surveillance intervals after an abnormal result, which a clinician sets by editing the item, ',
    'the individualised decisions the guidelines describe for ages 76 to 85 (colorectal) and 75 and older (breast), ',
    'supplemental breast imaging for dense breasts (insufficient evidence), and self-collected HPV testing. ',
    'A study recorded by name alone is found for colonoscopy, mammography and cytology; a stool test, HPV test or ',
    'imaging filed without a code is not, and anchors nothing.',
  ].join(''),
  useContext: [],
  // What kind of topic this protocol owns. The committer copies it onto the topic's `category`, so
  // the Prevention topic names itself on the resource and a reader or a search can pick it out
  // without inferring anything from the canonical.
  topic: [{ coding: [carePlanTopicKindCoding(PREVENTION_TOPIC_KIND)] }],
  relatedArtifact: [derivedFrom(USPSTF.colorectal), derivedFrom(USPSTF.breast), derivedFrom(USPSTF.cervical)],
  action: [
    {
      id: 'prevention',
      title: 'Cancer screening',
      action: [colonoscopy, fit, stoolDna, ctColonography, flexibleSigmoidoscopy, fobt, mammogram, pap, hpv],
    },
  ],
};

/** The categories this protocol screens for, and the modality actions under each, in table order. */
export const PREVENTION_SCREENING_ACTIONS: Record<ScreeningCategoryCode, readonly PlanDefinitionAction[]> = {
  'colorectal-cancer': [colonoscopy, fit, stoolDna, ctColonography, flexibleSigmoidoscopy, fobt],
  'breast-cancer': [mammogram],
  'cervical-cancer': [pap, hpv],
};
